import React, { useEffect, useMemo, useRef } from 'react';
import { Building2, Contact, ListRestart, MessagesSquare, Plus, Settings2 } from 'lucide-react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useParams, Link, useLocation, Redirect } from 'react-router-dom';
import { useGetSharedListQuery } from 'services/contacts';
import { useFailedDomains } from 'features/list';
import { Button } from '@getro/rombo';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import moment from 'moment';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import { contactsV2Api } from '../../services/contactsV2';
import { ShareList } from './shareList';
import usePageTitle from '../../hooks/usePageTitle';
import ContactsLayout from './layout/contactsLayout';
import CompaniesLayout from './layout/companiesLayout';
import { SharedListsActions } from '../../components/molecules/sharedListsActions';

const ListDetail = () => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const { listId } = useParams();
  const {
    data: { sharedList } = {},
    refetch: refetchSharedList,
    isLoading: isLoadingSharedList,
  } = useGetSharedListQuery({ listId });
  const pollingRef = useRef(null);
  const { search } = useLocation();
  const dispatch = useDispatch();
  const containerProps =
    sharedList?.kind === 'companies' ? { minHeight: '100vh', pb: '0px' } : { pb: '0px', height: '100%' };
  const { failedDomains } = useFailedDomains(true);

  useEffect(() => {
    const cleanup = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };

    if (sharedList?.refreshInProgress || sharedList?.operationInProgress) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          refetchSharedList();
        }, 3000);
      }
    } else if (sharedList && !sharedList.refreshInProgress && !sharedList.operationInProgress && pollingRef.current) {
      cleanup();
      dispatch(contactsV2Api.util.invalidateTags([{ type: 'contacts', id: parseInt(listId, 10) }]));
    }

    return () => cleanup();
  }, [sharedList?.operationInProgress, sharedList?.refreshInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const listName = useMemo(() => {
    const { name } = sharedList || {};
    return name;
  }, [sharedList]);

  const headerLinks = [
    {
      label: 'List settings',
      to: `/networks/${activeNetwork.slug}/list/${listId}/contacts/settings${search}`,
      variant: 'link',
      icon: Settings2,
      key: 'edit-link',
      color: 'text.main',
    },
    {
      label: 'Add companies',
      to: `/networks/${activeNetwork.slug}/list/${listId}/contacts/settings/add-companies${search}`,
      variant: 'link',
      icon: Plus,
      key: 'add-companies-link',
      color: 'text.main',
    },
    {
      label: 'Ask for intros',
      to: `/networks/${activeNetwork.slug}/list/${listId}/contacts/settings/ask-for-intros${search}`,
      variant: 'accent',
      icon: MessagesSquare,
      key: 'ask-for-intros-link',
      color: 'purple.500',
    },
  ];

  const headerStatus = [
    {
      label: sharedList?.kind === 'companies' ? 'Companies' : 'People',
      icon: sharedList?.kind === 'companies' ? Building2 : Contact,
      useTooltip: false,
      show: true,
    },
    {
      label: 'Auto-update',
      icon: ListRestart,
      show: sharedList?.smartOptions?.includes('auto_update') && sharedList?.kind === 'people',
      useTooltip: true,
      content: (
        <Flex
          tx="popover"
          variant="tooltip.content"
          sx={{
            gap: '12px',
            cursor: 'pointer',
            flexDirection: 'column',
            fontSize: '14px',
            color: 'neutral.0',
            fontWeight: '400',
          }}
        >
          <Text>
            Contacts in this list are added and removed daily to match your filters, while preserving manual edits.
          </Text>
          <Text>
            {sharedList?.smartSharedList?.populatedOn
              ? `Last updated ${moment(sharedList?.smartSharedList?.populatedOn).fromNow()}. Refresh now from Settings.`
              : 'Not yet updated. Refresh now from Settings.'}
          </Text>
        </Flex>
      ),
    },
  ].filter(({ show }) => show);

  usePageTitle(listName);

  if (!isLoadingSharedList && !sharedList) {
    return <Redirect to={`/networks/${activeNetwork.slug}/lists`} />;
  }

  return (
    <MainLayout containerProps={containerProps}>
      <MainLayoutHeader
        headerProps={{
          py: '12px',
        }}
        title={
          <Flex alignItems="center" sx={{ gap: '8px', maxWidth: '100%' }}>
            <Text as="span" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
              {isLoadingSharedList ? 'Loading...' : sharedList?.name || 'List not found'}
            </Text>
            {!isLoadingSharedList &&
              sharedList &&
              headerStatus.map(({ label, icon, useTooltip, content }) => (
                <DynamicTooltip
                  key={label}
                  useTooltip={useTooltip}
                  sx={{
                    maxWidth: '440px!important',
                    p: '4px 8px!important',
                  }}
                  trigger={
                    <Flex
                      alignItems="center"
                      bg="neutral.50"
                      sx={{ gap: '4px', borderRadius: 'circle', px: '8px', py: '2px' }}
                    >
                      <Box as={icon} height="12px" width="12px" minWidth="12px" minHeight="12px" aria-hidden="true" />
                      <Text color="text.main" fontSize="12px" fontWeight="500" lineHeight="16px">
                        {label}
                      </Text>
                    </Flex>
                  }
                  content={content}
                  position="bottom-end"
                />
              ))}
          </Flex>
        }
        actions={[
          ...(sharedList?.kind === 'companies'
            ? [
                <SharedListsActions
                  key="list-actions"
                  list={sharedList}
                  isManager={activeNetwork?.isManager}
                  triggerStyle={{ justifyContent: 'flex-end' }}
                  menuIconOrientation="vertical"
                  showDuplicateList={false}
                />,
                ...headerLinks.map(({ variant, icon, key, label, to, color }) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      svg: { color },
                    }}
                  >
                    <Button
                      variant={variant}
                      as={Link}
                      icon={icon}
                      size="small"
                      iconGap="8px"
                      sx={{
                        p: '0px 8px 0px 8px',
                        color,
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '16px',
                        textDecoration: 'none',
                      }}
                      to={to}
                    >
                      {label}
                    </Button>
                  </Box>
                )),
              ]
            : [
                <SharedListsActions
                  key="list-actions"
                  list={sharedList}
                  isManager={activeNetwork?.isManager}
                  triggerStyle={{ justifyContent: 'flex-end' }}
                  menuIconOrientation="vertical"
                />,
                <Button
                  variant="tertiary"
                  as={Link}
                  to={`${window.location.pathname.replace('/app', '')}/lists/settings${window.location.search}`}
                  size="small"
                  icon={Settings2}
                  iconGap="8px"
                  sx={{ p: '0px 8px 0px 8px' }}
                >
                  Settings
                </Button>,
                <ShareList key="shared-list-menu" sharedList={sharedList} />,
              ]),
        ]}
        breadcrumbs={[
          {
            label: 'Lists',
            to: `/networks/${activeNetwork.slug}/lists`,
          },
        ]}
      />
      {!isLoadingSharedList && !sharedList && (
        <Flex pt="80px" alignItems="center" justifyContent="center" flexDirection="column" sx={{ gap: '8px' }}>
          <Text fontWeight="500" fontSize="14px" color="text.dark">
            List not found
          </Text>
          <Text fontSize="14px">This link may be invalid or the list has been deleted.</Text>
        </Flex>
      )}
      {sharedList?.kind === 'people' && (
        <ContactsLayout sharedList={sharedList} activeNetwork={activeNetwork} listId={listId} listName={listName} />
      )}
      {sharedList?.kind === 'companies' && (
        <CompaniesLayout
          sharedList={sharedList}
          activeNetwork={activeNetwork}
          listId={listId}
          isLoadingSharedList={isLoadingSharedList}
          failedDomains={failedDomains}
        />
      )}
    </MainLayout>
  );
};

export default ListDetail;
